import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '../lib/api';
import toast from 'react-hot-toast';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  lastLoginAt?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isDemo: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  updateProfile: (data: { name: string; email: string }) => Promise<boolean>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// デモアカウントかどうかを判定
const isDemoUser = (userId: string) => userId === 'demo-admin';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // 起動時にセッションをチェック
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch {
        localStorage.removeItem('user');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await authApi.login({ email, password });
      const userData = response.data.user;
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      toast.success('ログインしました');
      return true;
    } catch (error: any) {
      const message = error.response?.data?.error || 'ログインに失敗しました';
      toast.error(message);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    toast.success('ログアウトしました');
  };

  const updateProfile = async (data: { name: string; email: string }): Promise<boolean> => {
    if (!user) return false;

    // デモアカウントの場合はローカルのみで更新
    if (isDemoUser(user.id)) {
      const updatedUser = { ...user, ...data };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      toast.success('プロフィールを更新しました');
      return true;
    }

    try {
      const response = await authApi.updateProfile(user.id, data);
      const updatedUser = response.data.user;
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      toast.success('プロフィールを更新しました');
      return true;
    } catch (error: any) {
      const message = error.response?.data?.error || 'プロフィールの更新に失敗しました';
      toast.error(message);
      return false;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    if (!user) return false;

    // デモアカウントの場合は成功を返す（実際のパスワードはない）
    if (isDemoUser(user.id)) {
      toast.success('パスワードを変更しました（デモモード）');
      return true;
    }

    try {
      await authApi.changePassword(user.id, { currentPassword, newPassword });
      toast.success('パスワードを変更しました');
      return true;
    } catch (error: any) {
      const message = error.response?.data?.error || 'パスワードの変更に失敗しました';
      toast.error(message);
      return false;
    }
  };

  const refreshUser = async () => {
    if (!user) return;
    
    // デモアカウントの場合は何もしない
    if (isDemoUser(user.id)) return;

    try {
      const response = await authApi.getMe(user.id);
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch {
      // エラー時はログアウト
      logout();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        isDemo: user ? isDemoUser(user.id) : false,
        login,
        logout,
        updateProfile,
        changePassword,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
